import {
  PopoverPosition as BlueprintPopoverPosition,
  Pre as BlueprintPre,
} from '@blueprintjs/core';
import { Code, Copy, Roadmap } from '@carbon/icons-react';
import { noop } from 'lodash';
import * as React from 'react';

import { QueryBuilderContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilder';
import { QueryBuilderModelContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderModel';
import { QueryBuilderSchemaContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderSchema';
import { TopExpressionBlock } from 'bones/components/molecules/QueryBuilder/TopExpressionBlock';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import {
  VoiceboxResponseActionType,
  VoiceboxResponseMessage,
} from 'bones/constants/voicebox/message';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { Accordion } from 'bones/design-system/components/Accordion';
import { StaticBadge } from 'bones/design-system/components/badge/StaticBadge';
import { Button } from 'bones/design-system/components/Button';
import { SystemMessage } from 'bones/design-system/components/notifications/SystemMessage';
import { QueryTokens } from 'bones/design-system/components/QueryTokens';
import { Tab } from 'bones/design-system/components/tabs/Tab';
import { Tabs } from 'bones/design-system/components/tabs/Tabs';
import { Tooltip } from 'bones/design-system/components/Tooltip';
import { Body } from 'bones/design-system/components/typography/Body';
import { addDraftFields } from 'bones/utils/queryBuilder/helpers/draft';
import { sparql } from 'bones/utils/queryBuilder/parsing/sparql';

const enum AccordionTab {
  CODE = 'CODE',
  QUERY_BUILDER = 'QUERY_BUILDER',
}

export interface VoiceboxPanelQueryExplanationProps {
  message: VoiceboxResponseMessage;
  settings: VoiceboxSettings;
}

export const VoiceboxPanelQueryExplanation: React.VFC<
  VoiceboxPanelQueryExplanationProps
> = ({ message, settings }) => {
  const {
    copy,
    testIds,
    queryBuilder: queryBuilderSchema,
    clipboard,
  } = useVoiceboxSchema();
  const { databasesById, modelsByDatabaseId } = useVoiceboxSettings();

  const [isOpen, setIsOpen] = React.useState(false);
  const [tab, setTab] = React.useState<AccordionTab>(
    AccordionTab.QUERY_BUILDER
  );

  //  istanbul ignore if
  if (!queryBuilderSchema) {
    return null;
  }

  const sparqlQueryActions = message.actions?.filter(
    (action) => action.type === VoiceboxResponseActionType.SPARQL
  );
  const sparqlQueryAction = sparqlQueryActions?.[0];

  if (!sparqlQueryAction) {
    return null;
  }

  const { value: query } = sparqlQueryAction;

  const model = modelsByDatabaseId[settings.databaseId]?.[
    settings.model || DEFAULT_MODEL_NAME
  ] || { classesByIri: {}, propertiesByIri: {} };
  const results = sparql(
    query,
    model,
    databasesById[settings.databaseId]?.namespaces || {}
  );

  const code = (
    <>
      <Body className="vb-voicebox-panel-query-explanation-label" size="md">
        {copy.panel.queryExplanation.sparql}
      </Body>
      <BlueprintPre>
        {results ? (
          <QueryTokens
            allTokens={results.tokens.all}
            ignoredTokens={results.tokens.ignored}
            unprocessedTokens={results.tokens.unprocessed}
          />
        ) : (
          query
        )}
      </BlueprintPre>
    </>
  );

  const queryBuilder = results ? (
    <>
      <Body className="vb-voicebox-panel-query-explanation-label" size="md">
        {copy.panel.queryExplanation.queryBuilder}
      </Body>
      <QueryBuilderSchemaContext.Provider value={queryBuilderSchema}>
        <QueryBuilderModelContext.Provider value={model}>
          <QueryBuilderContext.Provider
            value={{ topExpression: addDraftFields(results.topExpression) }}
          >
            <TopExpressionBlock setTopExpression={noop} />
          </QueryBuilderContext.Provider>
        </QueryBuilderModelContext.Provider>
      </QueryBuilderSchemaContext.Provider>
      {results.score === 1 ? null : (
        <SystemMessage
          className="vb-voicebox-panel-query-explanation-system-message"
          text={copy.queryBuilder.incomplete}
          variant="info"
        />
      )}
    </>
  ) : null;

  return (
    <Accordion
      actions={
        <>
          <StaticBadge size="md" type="info" variant="muted">
            {copy.panel.queryExplanation.beta}
          </StaticBadge>
          <Tooltip
            description={copy.panel.queryExplanation.copy}
            position={BlueprintPopoverPosition.BOTTOM}
          >
            {clipboard.isClipboardEnabled ? (
              <Button
                leftIcon={Copy}
                onClick={() => {
                  clipboard.copyToClipboard(query);
                }}
                size="sm"
                testId={testIds.panel.queryExplanation.copy}
                variant="ghost-gray"
              />
            ) : null}
          </Tooltip>

          {results ? (
            <Tabs size="sm" variant="square">
              <Tooltip
                description={copy.panel.queryExplanation.queryBuilder}
                position={BlueprintPopoverPosition.BOTTOM}
              >
                <Tab
                  leftIcon={Roadmap}
                  onClick={() => {
                    setTab(AccordionTab.QUERY_BUILDER);
                    setIsOpen(true);
                  }}
                  selected={tab === AccordionTab.QUERY_BUILDER}
                  size="sm"
                  testId={testIds.panel.queryExplanation.tabs.queryBuilder}
                />
              </Tooltip>
              <Tooltip
                description={copy.panel.queryExplanation.sparql}
                position={BlueprintPopoverPosition.BOTTOM}
              >
                <Tab
                  leftIcon={Code}
                  onClick={() => {
                    setTab(AccordionTab.CODE);
                    setIsOpen(true);
                  }}
                  selected={tab === AccordionTab.CODE}
                  size="sm"
                  testId={testIds.panel.queryExplanation.tabs.sparql}
                />
              </Tooltip>
            </Tabs>
          ) : null}
        </>
      }
      className="vb-voicebox-panel-query-explanation"
      icon="left"
      isOpen={isOpen}
      label={copy.panel.queryExplanation.accordion}
      onInteraction={(nextIsOpen: boolean) => setIsOpen(nextIsOpen)}
      size="md"
      testId={testIds.panel.queryExplanation.accordion}
    >
      {tab === AccordionTab.QUERY_BUILDER && queryBuilder ? queryBuilder : code}
    </Accordion>
  );
};
